import { useState, useEffect, useMemo } from "react";
import { Button } from "V2.0/common/button";
import { Field, Label, Description } from "V2.0/common/fieldset";
import { Checkbox, CheckboxField, CheckboxGroup } from "V2.0/common/checkbox";
import { getKnowledgebases } from "V2.0/HomePages/Knowledge_base/service";
import { putRAGSettings } from "../../service";
import { Heading } from "V2.0/common/heading";
import { message } from "antd";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

export function useKnowledeBase( workspaceId) {
  const [availableKnowledgeBases, setAvailableKnowledgeBases] = useState({
    data: [],
    isLoading: true,
  });

  const fetchKnowledgeBases = async () => {
    try {
      const response = await getKnowledgebases({ projectId: workspaceId });
      if (response.status === 200) {
        const data = response.data;
        setAvailableKnowledgeBases({
          data: data.knowledgebases,
          isLoading: false,
        });
      }
    } catch (e) {
      setAvailableKnowledgeBases((prevState) => {
        return {
          ...prevState,
          isLoading: false,
        };
      });
      console.error("Error");
    }
  };

  useEffect(() => {
    if (workspaceId) {
      fetchKnowledgeBases();
    }
  }, [workspaceId]);

  return { availableKnowledgeBases };
}

function KnowledgeBase(props) {
  const {
    ragSettings,
    syncAssistant,
    assistantId,
    workspaceId,
    locked,
    assistantType,
  } = props;
  const { knowledgeBases } = ragSettings;
  const [availableKnowledgeBases, setAvailableKnowledgeBases] = useState({
    data: [],
    isLoading: true,
  });
  const [selectedKnowledgeBases, setSelectedKnowledgeBases] = useState([]);

  useEffect(() => {
    setSelectedKnowledgeBases(knowledgeBases);
  }, [knowledgeBases]);

  const fetchKnowledgeBases = async () => {
    try {
      const response = await getKnowledgebases({ projectId: workspaceId });
      if (response.status === 200) {
        const data = response.data;
        setAvailableKnowledgeBases({
          data: data.knowledgebases,
          isLoading: false,
        });
      }
    } catch (e) {
      setAvailableKnowledgeBases((prevState) => {
        return {
          ...prevState,
          isLoading: false,
        };
      });
      console.error("Error");
    }
  };

  useEffect(() => {
    if (workspaceId) {
      fetchKnowledgeBases();
    }
  }, [workspaceId]);

  useEffect(() => {
    if (availableKnowledgeBases.data.length > 0) {
      const availableKnowledgeBaseIds = availableKnowledgeBases.data.map(
        (kb) => kb._id,
      );
      setSelectedKnowledgeBases((prevKnowledgeBases) => {
        return prevKnowledgeBases.filter((kbId) =>
          availableKnowledgeBaseIds.includes(kbId),
        );
      });
    }
  }, [availableKnowledgeBases]);

  const handleSave = async () => {
    message.loading("Updating Knowledge base list");
    const payload = {
      knowledgeBases: selectedKnowledgeBases,
    };
    try {
      const response = await putRAGSettings({
        workspaceId,
        assistantId,
        payload,
      });
      if (response.status === 200) {
        message.destroy();
        message.success("Knowledge base list updated");
        syncAssistant((prevAssistant) => {
          return {
            ...prevAssistant,
            ragSettings: response.data,
          };
        });
      }
    } catch (e) {
      message.destroy();
      message.error(
        e.response.data?.msg ||
          e.response.data?.message ||
          e.response.data?.error ||
          "Failed to update knowledge base list",
      );
      console.error("Error saving knowledge bases");
    }
  };

  const knowledgeBasesList = useMemo(() => {
    if (availableKnowledgeBases.data.length > 0) {
      return availableKnowledgeBases.data.map((kb) => {
        return {
          id: kb._id,
          label: kb.label,
          isSelected: selectedKnowledgeBases.includes(kb._id),
          disabled:
            selectedKnowledgeBases.length === 3 &&
            !selectedKnowledgeBases.includes(kb._id),
        };
      });
    }
    return [];
  }, [selectedKnowledgeBases]);

  return (
    <div className="flex flex-col gap-y-4 min-w-[500px] w-1/3 h-full mx-auto my-4 p-4 min-h-0">
      <div className="flex justify-between items-center">
        <Heading>Knowledge Bases (Max 3)</Heading>
        {!locked && <Button onClick={handleSave}>Save</Button>}
      </div>
      {knowledgeBasesList.length > 0 ? (
        <div className="border rounded-lg h-0 min-h-0 flex-1 overflow-auto p-4">
          <CheckboxGroup className="h-full min-h-0 flex flex-col gap-y-4">
            {knowledgeBasesList.map((kb) => {
              let handleChange;

              const unselectKnowledgeBase = () => {
                const idx = selectedKnowledgeBases.findIndex(
                  (_kb) => _kb === kb.id,
                );
                setSelectedKnowledgeBases((prevState) => {
                  return prevState.toSpliced(idx, 1);
                });
              };

              const selectKnowledgeBase = () => {
                setSelectedKnowledgeBases((prevState) => {
                  return [...prevState, kb.id];
                });
              };

              handleChange = kb.isSelected
                ? unselectKnowledgeBase
                : selectKnowledgeBase;

              return (
                <CheckboxField key={kb.id}>
                  <Checkbox
                    name={kb.id}
                    checked={kb.isSelected}
                    disabled={kb.disabled || locked}
                    onChange={handleChange}
                  />
                  <p className="text-base font-medium">{kb.label}</p>
                </CheckboxField>
              );
            })}
          </CheckboxGroup>
        </div>
      ) : (
        <div className="flex justify-center items-center h-full border rounded-lg">
          {availableKnowledgeBases.isLoading ? (
            <p>
              <AiOutlineLoading3Quarters className="animate-spin" />
            </p>
          ) : (
            <p className="text-sm">No Knowledge bases created</p>
          )}
        </div>
      )}
    </div>
  );
}

export { KnowledgeBase };
